/**
 * Canonical SKILL-AUTONOMY vocabulary — single source of truth (DEF-LLC-VOCAB-001).
 *
 * <p>Before this module, the *skill autonomy* concept (how much latitude a
 * skill is granted) was written three+ different ways across surfaces:
 *   • create slideout  → HUMAN_CONTROLLED / PLATFORM_CONTROLLED / SELF_AUTONOMOUS
 *   • edit form/decide → human_approval / supervised / autonomous
 *   • RBAC map         → HUMAN_CONTROLLED / SUPERVISED / ASSISTED
 *   • backend default  → "supervised"
 * Same concept, incompatible enums — so a value written by one surface didn't
 * match another's <select> options (the "autonomy shows wrong value" bug).
 *
 * <p>This module mirrors the pattern <code>constants/skillTypes.js</code>
 * already established for skill types: every surface imports the canonical
 * values + labels here and never hard-codes its own list. Legacy/synonym
 * values are folded to canonical via {@link normalizeAutonomy} on read and
 * write, so old data renders correctly and new writes are canonical.
 *
 * <p><b>Scope:</b> this is ONLY the per-skill autonomy axis. It intentionally
 * does NOT cover the distinct governance axes that happen to share words:
 *   • Agent trust band  — SUPERVISED / RESTRICTED / PROBATION / FULL (earned
 *     trust, score-waterfall managed, V188 CHECK constraints) — leave as-is.
 *   • Agent operation mode — platform / human / autonomous — leave as-is.
 *   • Decide review decision — autonomous / human_approval / blocked — its own
 *     axis by design.
 */

/** Canonical skill-autonomy values. Order = display order. */
export const SKILL_AUTONOMY = {
  HUMAN_CONTROLLED: 'HUMAN_CONTROLLED',
  PLATFORM_CONTROLLED: 'PLATFORM_CONTROLLED',
  SELF_AUTONOMOUS: 'SELF_AUTONOMOUS',
};

/** Canonical options for <select> menus: { value, label, hint }. */
export const SKILL_AUTONOMY_OPTIONS = [
  {
    value: SKILL_AUTONOMY.HUMAN_CONTROLLED,
    label: 'Human-controlled (recommended)',
    hint: 'Every execution surfaces for human approval before it runs.',
  },
  {
    value: SKILL_AUTONOMY.PLATFORM_CONTROLLED,
    label: 'Platform-controlled',
    hint: 'Platform-orchestrated, protected by policy; no per-call human approval.',
  },
  {
    value: SKILL_AUTONOMY.SELF_AUTONOMOUS,
    label: 'Self-autonomous',
    hint: 'The agent decides when to run this skill, within policy + guardrails.',
  },
];

/** Short human label for a (possibly legacy) autonomy value. */
export function autonomyLabel(value) {
  const v = normalizeAutonomy(value);
  return (SKILL_AUTONOMY_OPTIONS.find((o) => o.value === v) || {}).label || (value || '—');
}

/**
 * G273 — "Does this skill need a human before it fires?"
 *
 * MIRROR of the backend's single canonical predicate, `Skill.requiresHumanApproval()`. The
 * platform's rule is displayed == enforced, so the HUMAN GATE badge must be computed from
 * exactly the inputs the runtime pauses on — not a lookalike. Keep the two in lockstep:
 *   autonomy HUMAN_CONTROLLED (incl. legacy `human_approval`)
 *   OR inferenceMode HUMAN_APPROVAL
 *   OR an explicit approval guardrail on the skill.
 *
 * @param {{autonomy?: string, inferenceMode?: string, guardrails?: object}} skill
 * @returns {boolean}
 */
export function requiresHumanApproval(skill) {
  if (!skill) return false;
  const humanControlled = normalizeAutonomy(skill.autonomy) === SKILL_AUTONOMY.HUMAN_CONTROLLED;
  const humanInference = String(skill.inferenceMode || '').toUpperCase() === 'HUMAN_APPROVAL';
  return humanControlled || humanInference || hasApprovalGuardrail(skill.guardrails);
}

/**
 * `guardrails` is a free-form JSONB column and it is POLYMORPHIC in real data:
 *
 *   object form → { "audit_log": true, "inputValidation": true }
 *   array form  → [ "audit_log", "approval_required" ]          ← seen on isolateHost
 *
 * Reading `guardrails.approval_required` off the array form silently yields undefined, so a
 * skill that genuinely carried the approval guardrail read as "no human gate". Handle both
 * shapes rather than assuming the one we happened to look at first. (An object value is only
 * honoured when truthy — `{approval_required: false}` must NOT gate.)
 */
function hasApprovalGuardrail(guardrails) {
  if (!guardrails) return false;
  const KEYS = ['approval_required', 'approvalRequired'];
  if (Array.isArray(guardrails)) {
    return guardrails.some((g) => KEYS.includes(String(g)));
  }
  if (typeof guardrails === 'object') {
    return KEYS.some((k) => !!guardrails[k]);
  }
  return false;
}

/**
 * Fold any legacy / synonym skill-autonomy value to the canonical enum.
 * Case- and separator-insensitive. Unknown values pass through unchanged so
 * we never silently lose data (the UI shows them via a defensive option).
 *
 * @param {string} value
 * @returns {string} canonical value, or the original if unrecognised
 */
export function normalizeAutonomy(value) {
  if (!value) return value;
  const k = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  switch (k) {
    // human-in-the-loop synonyms
    case 'human_controlled':
    case 'human_approval':
    case 'human':
    case 'supervised':
    case 'assisted':
      return SKILL_AUTONOMY.HUMAN_CONTROLLED;
    // platform-orchestrated synonyms
    case 'platform_controlled':
    case 'platform':
      return SKILL_AUTONOMY.PLATFORM_CONTROLLED;
    // full-autonomy synonyms
    case 'self_autonomous':
    case 'autonomous':
    case 'self':
    case 'full':
      return SKILL_AUTONOMY.SELF_AUTONOMOUS;
    default:
      return value;
  }
}
